import { App } from '@afinius/api-client/landlord-application/types';
import { tenantApi } from '@afinius/api-client/tenant/api';
import type { Tenant } from '@afinius/api-client/tenant/types';
import { setTenantUuid } from '@afinius/api-client/tenant-uuid';
import { ApiError } from '@afinius/types/Error';
import { redirectToApp } from '@afinius/utilities/app-url';
import { isCapacitor } from '@afinius/utilities/mobile';
import { message } from 'ant-design-vue';
import { defineStore } from 'pinia';
import { type Ref, ref } from 'vue';
import { useRouter } from 'vue-router';

type Callback = () => void;

interface Store {
  tenant: Ref<Tenant | undefined>;
  fetchTenant: () => Promise<void>;
  onFetched: (callback: Callback) => void;
}

export const useTenantStore = defineStore('tenant', (): Store => {
  const router = useRouter();

  const tenant = ref<Tenant>();
  const callbacks = ref<Callback[]>([]);

  const handleTenantNotFound = () => {
    if (!isCapacitor) {
      redirectToApp(App.AfiniusAuth);
      return;
    }

    setTenantUuid(undefined);
    router.replace({ name: 'tenant.index' });
  };

  const fetchTenant = async () => {
    tenant.value = await tenantApi.current().catch((error: ApiError) => {
      if (error.response?.status === 404) {
        handleTenantNotFound();
      } else if (error.response?.status !== 503 && error.response?.data) {
        message.error(error.response.data.message);
      }

      return undefined;
    });

    callbacks.value.forEach((callback) => callback());
  };

  const onFetched = (callback: Callback) => {
    callbacks.value.push(callback);
  };

  return {
    tenant,
    fetchTenant,
    onFetched,
  };
});
